import {
  LiveKitRoom,
  RoomAudioRenderer,
  useConnectionState,
  useDataChannel,
  useIsSpeaking,
  useLocalParticipant,
  useRemoteParticipants
} from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';
import { CheckCircle, Loader2, Mic, MicOff } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SaraAvatar } from './sara-avatar';

import { Button } from '~/components/ui/button';
import { InterviewApiError, connectVoiceInterview } from '~/lib/interview-api';

interface VoiceInterfaceProps {
  token: string;
  language: string;
  jobTitle: string;
  employer: string | null;
  candidateName: string;
  onCompleted: () => void;
}

export function VoiceInterface({
  token,
  language,
  jobTitle,
  employer,
  candidateName,
  onCompleted
}: VoiceInterfaceProps): React.ReactNode {
  const { t } = useTranslation();
  const [connectionInfo, setConnectionInfo] = useState<{
    participantToken: string;
    livekitUrl: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(true);

  useEffect(() => {
    async function connect(): Promise<void> {
      try {
        const info = await connectVoiceInterview(token, language);
        setConnectionInfo(info);
        setConnecting(false);
      } catch (err) {
        // 409 means the session was already claimed by the other strict-mode mount — ignore
        if (err instanceof InterviewApiError && err.status === 409) return;
        setError(err instanceof Error ? err.message : 'Failed to connect');
        setConnecting(false);
      }
    }

    void connect();
  }, [token, language]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm text-center">
          <p className="text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  if (connecting || !connectionInfo) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t('screening.voice.connecting')}</p>
        </div>
      </div>
    );
  }

  return (
    <LiveKitRoom
      token={connectionInfo.participantToken}
      serverUrl={connectionInfo.livekitUrl}
      connect={true}
      audio={true}
      video={false}
    >
      <VoiceSession
        jobTitle={jobTitle}
        employer={employer}
        candidateName={candidateName}
        onCompleted={onCompleted}
      />
      <RoomAudioRenderer />
    </LiveKitRoom>
  );
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function ParticipantCard({
  name,
  initials,
  isSpeaking,
  variant
}: {
  name: string;
  initials: string;
  isSpeaking: boolean;
  variant: 'ai' | 'candidate';
}): React.ReactNode {
  const speakingRing = variant === 'ai' ? 'ring-primary/40' : 'ring-foreground/20';

  return (
    <div
      className={`flex flex-1 flex-col items-center justify-center gap-4 rounded-2xl border bg-card p-8 transition-shadow ${
        isSpeaking ? `ring-[3px] ${speakingRing}` : 'border-border'
      }`}
    >
      {variant === 'ai' ? (
        <SaraAvatar size="lg" />
      ) : (
        <div className="flex size-24 items-center justify-center rounded-full bg-muted text-2xl font-semibold text-muted-foreground">
          {initials}
        </div>
      )}
      <span className="text-sm font-medium text-foreground">{name}</span>
    </div>
  );
}

function VoiceSession({
  jobTitle,
  employer,
  candidateName,
  onCompleted
}: {
  jobTitle: string;
  employer: string | null;
  candidateName: string;
  onCompleted: () => void;
}): React.ReactNode {
  const { t } = useTranslation();
  const connectionState = useConnectionState();
  const { localParticipant } = useLocalParticipant();
  const remoteParticipants = useRemoteParticipants();
  const [isMuted, setIsMuted] = useState(false);
  const [interviewFinished, setInterviewFinished] = useState(false);
  const [wasConnected, setWasConnected] = useState(false);
  const isConnected = connectionState === ConnectionState.Connected;

  const agentParticipant = remoteParticipants[0] ?? localParticipant;
  const localIsSpeaking = useIsSpeaking(localParticipant);
  const agentIsSpeaking = useIsSpeaking(agentParticipant);
  const hasAgent = remoteParticipants.length > 0;

  const candidateInitials = useMemo(() => getInitials(candidateName), [candidateName]);

  // Listen for completion data message from agent
  useDataChannel('interview-status', (msg) => {
    try {
      const data: unknown = JSON.parse(new TextDecoder().decode(msg.payload));
      if ((data as { type: string }).type === 'interview_complete') {
        setInterviewFinished(true);
      }
    } catch {
      // ignore malformed data
    }
  });

  // Track that we were connected at least once
  useEffect(() => {
    if (isConnected) setWasConnected(true);
  }, [isConnected]);

  // Fallback: if the room disconnects after being connected, show submit button
  useEffect(() => {
    if (wasConnected && connectionState === ConnectionState.Disconnected) {
      setInterviewFinished(true);
    }
  }, [wasConnected, connectionState]);

  const toggleMute = useCallback(() => {
    if (isMuted) {
      void localParticipant.setMicrophoneEnabled(true);
    } else {
      void localParticipant.setMicrophoneEnabled(false);
    }
    setIsMuted(!isMuted);
  }, [localParticipant, isMuted]);

  return (
    <div className="flex h-dvh flex-col bg-background">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3 sm:px-6">
        <div>
          <p className="text-lg font-semibold">{t('screening.voice.interviewInProgress')}</p>
          <p className="text-xs text-muted-foreground">
            {jobTitle}
            {employer ? ` — ${employer}` : ''}
          </p>
        </div>
        {isConnected && !interviewFinished && (
          <div className="flex items-center gap-2 rounded-full border border-green-200 bg-green-50 px-3 py-1">
            <span className="size-2 rounded-full bg-green-500" />
            <span className="text-xs font-medium text-green-700">
              {t('screening.voice.recording')}
            </span>
          </div>
        )}
      </div>

      {/* Main area */}
      <div className="flex flex-1 flex-col items-center justify-center px-4 py-6 sm:px-6">
        {!isConnected && !interviewFinished && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('screening.voice.connecting')}
          </div>
        )}

        {(isConnected || interviewFinished) && (
          <div className="grid w-full max-w-3xl flex-1 grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
            <ParticipantCard
              name={candidateName}
              initials={candidateInitials}
              isSpeaking={!interviewFinished && localIsSpeaking}
              variant="candidate"
            />
            <ParticipantCard
              name="Sara"
              initials="SA"
              isSpeaking={!interviewFinished && hasAgent && agentIsSpeaking}
              variant="ai"
            />
          </div>
        )}
      </div>

      {/* Bottom bar */}
      <div className="border-t border-border px-4 py-3 sm:px-6">
        <div className="relative mx-auto flex max-w-3xl items-center justify-center">
          {/* Left: technical issue link */}
          <button
            className="absolute left-0 text-xs text-muted-foreground underline-offset-2 hover:underline"
            onClick={() => {
              // Technical issue handling could be added here in the future
            }}
          >
            {t('screening.technicalIssue.buttonLabel')}
          </button>

          {/* Center: mute or finished indicator */}
          {!interviewFinished && isConnected && (
            <div className="flex flex-col items-center gap-1">
              <Button
                variant={isMuted ? 'destructive' : 'outline'}
                size="icon"
                className="size-12 rounded-full"
                onClick={toggleMute}
              >
                {isMuted ? <MicOff className="size-5" /> : <Mic className="size-5" />}
              </Button>
              {isMuted && <p className="text-xs text-destructive">{t('screening.voice.muted')}</p>}
            </div>
          )}
          {interviewFinished && (
            <Button className="h-11 gap-2 px-6" onClick={onCompleted}>
              <CheckCircle className="size-4" />
              {t('screening.submitInterview')}
            </Button>
          )}
        </div>

        {/* AI disclosure */}
        <p className="mt-2 text-center text-xs text-muted-foreground">
          {t('screening.aiDisclosure')}
        </p>
      </div>
    </div>
  );
}
